import { FC, useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import SimplePrivateRoute from './SimplePrivateRoute';
import { ChatsView, ConversationView } from '@vs-online-only-app/chats';
import { AppLayout } from '../AppLayout';
import { LoginView } from '../Login';
import ProfileSetupView from '../profile-setup/ProfileSetupView';
import IntroModulesView from '../intro-modules/IntroModulesView';
import EditProfileView from '../Views/edit-profile/EditProfileView';
import HomeView from '../Views/home/HomeView';
import PageNotFoundView from '../Views/PageNotFoundView';
import CreateAccountView from '../Views/create-account/CreateAccountView';
import ResetPasswordView from '../Views/reset-password/ResetPasswordView';

interface WindowMessageEvent extends MessageEvent {
  data: {
    eventName: string;
    path?: string; // path is optional because you may want to handle different types of messages
  };
}

const isStandalone = window.top === window;

const AppRouter: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isStandalone) return;
    window.top?.postMessage({
      eventName: 'navigatedTo',
      path: location.pathname,
    }, '*');
  }, [location]);

  useEffect(() => {
    if (isStandalone) return;
    window.top?.postMessage({
      eventName: 'delegatedNavigation',
      microFrontend: 'chat-web-app',
    }, '*');
  }, []);

  useEffect(() => {
    const parentWindowListener = (event: WindowMessageEvent) => {
      if (event.data && event.data.eventName === 'navigateTo' && event.data.path) {
        navigate(event.data.path);
      }
    };
    window.addEventListener('message', parentWindowListener);
    return () => {
      window.removeEventListener('message', parentWindowListener);
    };
  }, [navigate]);

  return (
    <AppLayout>
      <Routes>
        <Route
          path="/profile-setup"
          element={
            <PrivateRoute>
              <ProfileSetupView />
            </PrivateRoute>
          }
        />
        <Route
          path="/introduction"
          element={
            <PrivateRoute>
              <IntroModulesView />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <PrivateRoute>
              <EditProfileView />
            </PrivateRoute>
          }
        />
        <Route
          path="/chats"
          element={
            <PrivateRoute>
              <ChatsView />
            </PrivateRoute>
          }
        />
        <Route
          path="/conversation"
          element={
            <SimplePrivateRoute>
              <ConversationView />
            </SimplePrivateRoute>
          }
        />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <HomeView />
            </PrivateRoute>
          }
        />
        <Route path="/login" element={<LoginView />} />
        <Route path="/reset-password" element={<ResetPasswordView />} />
        <Route path="/account/create" element={<CreateAccountView />} />
        <Route path="*" element={<PageNotFoundView />} />
      </Routes>
    </AppLayout>
  );
};

export default AppRouter;
